import { router } from "@inertiajs/react"
import { useCallback, useEffect, useMemo, useState } from "react"

/**
 * Bulk-selection cap — matches the OKS-17 list page size.
 *
 * **CTPO-locked V1 invariant** (PRD §Acceptance + UX Spec §Flows):
 * `Select all on this page` selects `min(visible_rows, 25)`; manual
 * toggle past 25 is a no-op. The cap is structural (visible-row-count
 * never exceeds 25), so the only way to exceed it is a hand-crafted
 * URL — defence-in-depth for that path lives at the BE service
 * (`errors.candidate_ids: :too_many` flash) plus FE F4 stub-flash
 * inline alert. There is intentionally NO `candidate-list-bulk-cap-warning`
 * testid (joint freeze with ux-oks19 + qa-oks19; YAGNI per Phase 2
 * lock memo).
 */
export const BULK_SELECTION_CAP = 25

/**
 * Tri-state value for the `Select all on this page` header checkbox.
 *
 * - `false` → no visible rows selected (or no rows at all).
 * - `true` → every visible row is in the selection set.
 * - `"mixed"` → at least one but not every visible row is selected
 *   (Radix `aria-checked="mixed"` rendering + UX Spec §Micro-Interactions).
 */
export type SelectAllState = boolean | "mixed"

export interface UseBulkSelectionApi {
  /** Read-only handle on the current selection set. Pass to `bulk_transition[candidate_ids][]` form posts. */
  selected: ReadonlySet<number>
  /** Convenience — `selected.size`. Drives `candidate-list-bulk-bar` mount + `candidate-list-bulk-count` text. */
  size: number
  /** Cheap predicate for per-row checkbox `checked` binding. */
  isSelected: (id: number) => boolean
  /** Tri-state computation across the visible-row id list (drives header `aria-checked`). */
  selectAllState: (visibleIds: readonly number[]) => SelectAllState
  /** Per-row toggle. Hard-capped at `BULK_SELECTION_CAP` — adding a 26th id is a silent no-op. */
  toggle: (id: number) => void
  /**
   * Select-all-on-this-page semantics:
   *   - if every visible row is currently selected → unselect all visible
   *     (preserves any cross-page selections, though V1 has none — see
   *     PRD §Out-of-Scope: no select-across-pages V1).
   *   - else → select all visible rows up to the cap, merging with
   *     any prior selection.
   */
  selectAll: (visibleIds: readonly number[]) => void
  /** Hard reset. Wired to `candidate-list-bulk-clear` button in the bulk bar. */
  clear: () => void
  /**
   * **OKS-952** — narrow the selection to the intersection with `ids`.
   *
   * REMOVES ONLY. The post-call selection is always a subset of the
   * pre-call one; an id in `ids` that is not currently selected is ignored
   * rather than added.
   *
   * 🔴 **This exists because `toggle` cannot express narrowing safely.**
   * `toggle` on an unselected id ADDS it, so `idsToDrop.forEach(toggle)`
   * silently becomes a WIDENING the moment the drop-set contains anything
   * unselected — rejecting candidates the user never picked, atomically,
   * onto an append-only audit table. `retain` cannot do that: it only ever
   * intersects.
   *
   * ⚠️ The remove-only property holds BY CONSTRUCTION here, so nothing
   * downstream will ever assert it. `use-bulk-selection.test.tsx` carries the
   * arm that would red on a toggle-based implementation — a drop-set
   * containing an unselected id. Do not "simplify" this back to `toggle`.
   *
   * No cap check: narrowing can only shrink the set, so
   * `BULK_SELECTION_CAP` cannot be breached.
   */
  retain: (ids: readonly number[]) => void
}

/**
 * `useBulkSelection` — the OKS-19 bulk-action selection hook.
 *
 * **Hard rules baked in (FE TRD §State & Data Flow + CTPO Q3 lock):**
 *
 * 1. **Pure client React state.** No URL sync (`?selected=…` is
 *    explicitly rejected per CTPO; "share the filter, not the
 *    selection"). Selection lives in this hook for the lifetime of
 *    the page mount.
 * 2. **Resets on any `router.get`** — the hook subscribes to
 *    `router.on("start", …)` so EVERY Inertia visit (filter, sort,
 *    search, pagination, tab-switch, back-nav) clears the set
 *    automatically. This is the single source of truth for the
 *    selection-reset behaviour QA E12 covers as a Playwright
 *    regression × 5 sub-cases.
 * 3. **Hard cap at 25.** Matches the OKS-17 list page size; cap is
 *    structural since `visible_rows ≤ 25` always holds in V1.
 *    Manual toggle past 25 is a silent no-op (defence-in-depth).
 * 4. **Tri-state header.** `selectAllState(visibleIds)` returns
 *    `true | false | "mixed"` for the header `Select all on this
 *    page` checkbox `aria-checked` attribute (UX Spec
 *    §Micro-Interactions: `1 ≤ n < 25 → "mixed"`).
 *
 * Test coverage: `app/frontend/hooks/__tests__/use-bulk-selection.test.tsx`
 * — 5 sub-case selection-reset matrix (filter/sort/search/page/tab),
 * cap behaviour, tri-state matrix, toggle/select-all/clear.
 */
export function useBulkSelection(): UseBulkSelectionApi {
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set())

  // CTPO-locked: selection clears on EVERY Inertia visit. The
  // `router.on("start", …)` callback fires the moment a visit begins
  // (before the new props arrive), which is the right moment to
  // unmount the bulk action bar — UX Spec §Flows step 7.
  //
  // The mocked router in `app/frontend/test/setup.tsx` returns a
  // no-op teardown function from `router.on(...)`; following the
  // OKS-17 Index.tsx pattern (calls cleanup directly, no defensive
  // null-check) keeps consistency with the established codebase
  // style.
  useEffect(() => {
    const off = router.on("start", () => {
      setSelected((prev) => (prev.size === 0 ? prev : new Set()))
    })
    return () => {
      off()
    }
  }, [])

  const isSelected = useCallback((id: number) => selected.has(id), [selected])

  const toggle = useCallback((id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
        return next
      }
      // Hard cap: silently no-op if adding would exceed 25. The
      // visible-row-count constraint makes this unreachable from
      // the happy-path UI (Select all caps client-side too); this
      // branch is defensive-only.
      if (next.size >= BULK_SELECTION_CAP) return prev
      next.add(id)
      return next
    })
  }, [])

  // OKS-952 — see the interface docs: remove-only by construction.
  const retain = useCallback((ids: readonly number[]) => {
    const keep = new Set(ids)
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => keep.has(id)))
      // Identity-stable when nothing changed, matching `toggle`'s posture, so
      // a no-op narrow does not re-render every consumer of `selected`.
      return next.size === prev.size ? prev : next
    })
  }, [])

  const selectAll = useCallback((visibleIds: readonly number[]) => {
    setSelected((prev) => {
      // If every visible row is already selected, the click acts as
      // an "unselect visible" toggle (preserves any non-visible
      // selections — V1 has none, but the abstraction is kept clean
      // for future select-across-pages support — V2 follow-up).
      const allVisibleSelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id))
      if (allVisibleSelected) {
        const next = new Set(prev)
        visibleIds.forEach((id) => next.delete(id))
        return next
      }
      // Else: union with visible, capped at 25.
      const next = new Set(prev)
      for (const id of visibleIds) {
        if (next.size >= BULK_SELECTION_CAP) break
        next.add(id)
      }
      return next
    })
  }, [])

  const clear = useCallback(() => {
    setSelected((prev) => (prev.size === 0 ? prev : new Set()))
  }, [])

  const selectAllState = useCallback(
    (visibleIds: readonly number[]): SelectAllState => {
      if (visibleIds.length === 0) return false
      let count = 0
      for (const id of visibleIds) {
        if (selected.has(id)) count += 1
      }
      if (count === 0) return false
      if (count === visibleIds.length) return true
      return "mixed"
    },
    [selected],
  )

  return useMemo(
    () => ({
      selected,
      size: selected.size,
      isSelected,
      selectAllState,
      toggle,
      selectAll,
      clear,
      retain,
    }),
    [selected, isSelected, selectAllState, toggle, selectAll, clear, retain],
  )
}
